// ============================================================
// 生成 Schema（阶段一 T1）：数据集 + 规则链（dtwince.dataflow.schema.json）
// 落库映射：data_sources（数据集：连接+解析管线）+ rules（规则链：条件+动作）
// V1 安全子集：禁 parser.transform；filter 仅简单比较；conditions 仅标准算子；actions 白名单
// ============================================================
import { z } from 'zod';
import { DATASET_TYPES, GENERATION_SCHEMA_VERSION, GEN_LIMITS, RULE_ACTION_TYPES, RULE_OPERATORS } from './constants';
import { safeParserSchema } from './common';

/** 数据集（data_sources 表） */
export const datasetSchema = z
  .object({
    name: z.string().min(1).max(120),
    type: z.enum(DATASET_TYPES),
    config: z.record(z.unknown()).default({}),
    parser: safeParserSchema.optional(),
    refreshInterval: z.number().int().positive().max(3600000).optional(),
  })
  .strict();
export type DatasetSpec = z.infer<typeof datasetSchema>;

const ruleValueSchema = z.union([
  z.number(),
  z.string().max(500),
  z.boolean(),
  z.array(z.union([z.number(), z.string().max(200)])).max(50),
]);
export type RuleValue = z.infer<typeof ruleValueSchema>;

const ruleLeafSchema = z
  .object({
    fact: z.string().min(1).max(200),
    operator: z.enum(RULE_OPERATORS),
    value: ruleValueSchema,
  })
  .strict();

export type RuleCondition =
  | { fact: string; operator: (typeof RULE_OPERATORS)[number]; value: RuleValue }
  | { all: RuleCondition[] }
  | { any: RuleCondition[] };

/** 条件树：递归 all / any / 叶子（json-rules-engine 兼容） */
export const ruleConditionSchema: z.ZodType<RuleCondition> = z.lazy(() =>
  z.union([
    ruleLeafSchema,
    z.object({ all: z.array(ruleConditionSchema).min(1).max(20) }).strict(),
    z.object({ any: z.array(ruleConditionSchema).min(1).max(20) }).strict(),
  ]),
);

/** 规则动作（rule.types.ts RuleAction 对齐，仅白名单类型） */
export const ruleActionSchema = z
  .object({
    type: z.enum(RULE_ACTION_TYPES),
    params: z.record(z.unknown()).optional(),
  })
  .strict();
export type RuleActionSpec = z.infer<typeof ruleActionSchema>;

/** 规则链（rules 表） */
export const dataflowFlowSchema = z
  .object({
    name: z.string().min(1).max(120),
    enabled: z.boolean().default(true),
    priority: z.number().int().min(0).max(10000).default(100),
    eventCodes: z.array(z.string().max(64)).max(50).optional(),
    conditions: ruleConditionSchema,
    actions: z.array(ruleActionSchema).min(1).max(10),
    cooldownMs: z.number().int().min(0).max(86400000).default(0),
  })
  .strict();
export type DataflowFlowSpec = z.infer<typeof dataflowFlowSchema>;

export const dataflowBundleSchema = z
  .object({
    schemaVersion: z.literal(GENERATION_SCHEMA_VERSION),
    kind: z.literal('dataflow'),
    datasets: z.array(datasetSchema).max(GEN_LIMITS.datasets).default([]),
    flows: z.array(dataflowFlowSchema).max(GEN_LIMITS.flows).default([]),
  })
  .strict();

export type DataflowBundle = z.infer<typeof dataflowBundleSchema>;
